import { DecimalPipe } from '@angular/common';
import { Component, computed, OnInit, signal, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import {
  DatePipe,
  DateTimePipe,
  NimmstaTimeAgoPipe,
} from '@nimmsta/ng-commons-lib/pipes';
import {
  DeviceHistoryItem,
  Pageable,
  UserRole,
} from '@nimmsta/ng-commons-lib/models';
import { DeviceHistoryListComponent } from '@nimmsta/ng-commons-lib/device-history';
import { DeviceHistoryService } from '@nimmsta/ng-commons-lib/services/device-history';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { Scanner } from '../../models/scanner';
import { ScannerConnection } from '../../models/scannerConnection';
import { ScannerService } from '../../services/scanner.service';
import { CareArticle, Status, Type } from '../../models/care-article.model';
import { TranslatePipe } from '@ngx-translate/core';
import { ScannerSingleMaintenanceCheckBadgePipe } from './maintenance-check-badge.pipe';
import { ScannerDeviceMaintenanceStatusBadgePipe } from './maintenance-status-badge.pipe';
import { ScannerSoftwareStatusPipe } from './software-status.pipe';
import { ScannerStatusPipe } from './status.pipe';
import { deriveDisplayedChargingCycles } from './charging-cycles.util';
import dayjs from 'dayjs';
import { catchError, map, of, switchMap, take } from 'rxjs';

type ScannerSection =
  | 'maintenance'
  | 'technical'
  | 'operations'
  | 'warranty'
  | 'history';

@Component({
  templateUrl: 'scanner-detail.component.html',
  imports: [
    TranslatePipe,
    HeaderBarComponent,
    LoadingIndicatorComponent,
    ErrorComponent,
    NimmstaTimeAgoPipe,
    DatePipe,
    DateTimePipe,
    DecimalPipe,
    ScannerSingleMaintenanceCheckBadgePipe,
    ScannerStatusPipe,
    ScannerDeviceMaintenanceStatusBadgePipe,
    ScannerSoftwareStatusPipe,
    DeviceHistoryListComponent,
  ],
})
export class ScannerDetailComponent implements OnInit {
  private static readonly CONNECTION_DATA_FALLBACK_LIMIT = 10;

  // NIMRND-11132: Firmware bundles 9.0-9.2 report unreliable full charge capacity values.
  private static readonly BATTERY_HEALTH_UNRELIABLE_FIRMWARE_BUNDLE_PREFIXES = [
    '9.0',
    '9.1',
    '9.2',
  ];

  private scannerService = inject(ScannerService);
  private deviceHistoryService = inject(DeviceHistoryService);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);

  error?: HttpErrorResponse;

  device?: Scanner;

  lastConnection?: ScannerConnection;

  historyItems: DeviceHistoryItem[] = [];

  isLoading = false;

  isAdmin = false;

  readonly openSections = signal<Record<ScannerSection, boolean>>({
    maintenance: false,
    technical: false,
    operations: false,
    warranty: false,
    history: false,
  });

  allCareArticles = signal<CareArticle[]>([]);

  warrantyExtensions = computed(() =>
    this.allCareArticles().filter(
      (careArticle) => careArticle.type === Type.WARRANTY,
    ),
  );

  appliedCareArticles = computed(() =>
    this.allCareArticles().filter(
      (careArticle) =>
        careArticle.type === Type.CARE_ARTICLE &&
        careArticle.status === Status.APPLIED,
    ),
  );

  openCareArticles = computed(() =>
    this.allCareArticles()
      .filter(
        (careArticle) =>
          careArticle.type === Type.CARE_ARTICLE &&
          careArticle.status === Status.OPEN &&
          (!careArticle.purchasedDate ||
            !careArticle.purchasedDate
              .add(careArticle.months ?? 0, 'months')
              .isBefore(dayjs())),
      )
      .sort((a, b) => a.mpn.localeCompare(b.mpn)),
  );

  expiredCareArticles = computed(() =>
    this.allCareArticles()
      .filter(
        (careArticle) =>
          careArticle.type === Type.CARE_ARTICLE &&
          careArticle.status === Status.OPEN &&
          careArticle.purchasedDate &&
          careArticle.purchasedDate
            .add(careArticle.months ?? 0, 'months')
            .isBefore(dayjs()),
      )
      .sort((a, b) => a.mpn.localeCompare(b.mpn)),
  );

  expiryDateMap = computed(() => {
    const articles = this.allCareArticles();
    return articles.reduce(
      (map, article) => {
        if (article.purchasedDate && article.months) {
          const expiryDate = article.purchasedDate.add(
            article.months,
            'months',
          );
          return { ...map, [article.id]: expiryDate };
        }

        return map;
      },
      {} as Record<string, dayjs.Dayjs>,
    );
  });

  showFullWarrantyDates = signal(false);

  readonly firmwareVersionFields = [
    { key: 'firmwareVersion', label: 'scanner.firmwareVersion' },
    { key: 'bootloaderVersion', label: 'scanner.bootloaderVersion' },
    { key: 'bleScriptVersion', label: 'scanner.bleScriptVersion' },
    { key: 'bleFirmwareVersion', label: 'scanner.bleFirmwareVersion' },
    { key: 'imagerFirmwareVersion', label: 'scanner.imagerFirmwareVersion' },
    { key: 'touchFirmwareVersion', label: 'scanner.touchFirmwareVersion' },
    { key: 'hardwareVersion', label: 'scanner.hardwareVersion' },
    { key: 'protocolVersion', label: 'scanner.protocolVersion' },
  ] as const;

  readonly operationDataFields = [
    { key: 'chargingCycles', label: 'scanner.chargingCycles' },
    {
      key: 'fuelgaugeFullChargeCapacityInMah',
      label: 'scanner.fuelgaugeFullChargeCapacityInMah',
    },
    {
      key: 'fuelgaugeMaxTemperatureInDeciKelvin',
      label: 'scanner.fuelgaugeMaxTemperatureInDeciKelvin',
    },
    { key: 'numberOfChargingEvents', label: 'scanner.numberOfChargingEvents' },
    { key: 'numberOfScanEvents', label: 'scanner.numberOfScanEvents' },
    { key: 'numberOfTriggerEvents', label: 'scanner.numberOfTriggerEvents' },
    {
      key: 'systemOperationTimeInSeconds',
      label: 'scanner.systemOperationTimeInSeconds',
    },
  ] as const;

  ngOnInit(): void {
    this.isLoading = true;
    this.authService
      .getCurrentUser()
      .pipe(
        take(1),
        switchMap((user) => {
          this.isAdmin = user.role === UserRole.ADMIN;
          return this.route.params;
        }),
      )
      .subscribe((params) => {
        const deviceId = params['id'];
        this.historyItems = [];

        this.scannerService
          .get(deviceId)
          .pipe(
            switchMap((device) =>
              this.scannerService.resolveSoftwareStatuses([device]).pipe(
                map(() => device),
                catchError(() => of(device)),
              ),
            ),
          )
          .subscribe({
            next: (device: Scanner) => {
              this.device = device;
              if (device.careArticles) {
                this.allCareArticles.set(device.careArticles);
              }
              if (
                device.warrantyBaseEndDate &&
                device.warrantyFullEndDate &&
                !device.warrantyBaseEndDate.isSame(
                  device.warrantyFullEndDate,
                  'seconds',
                )
              ) {
                this.showFullWarrantyDates.set(true);
              }
              this.isLoading = false;
            },
            error: (error: HttpErrorResponse) => {
              this.error = error;
              this.isLoading = false;
            },
          });
        this.scannerService.getConnections(deviceId).subscribe({
          next: (page: Pageable<ScannerConnection>) => {
            this.lastConnection = this.mergeConnections(page.content);
          },
          error: (error: HttpErrorResponse) => {
            this.error = error;
          },
        });

        if (this.isAdmin) {
          this.deviceHistoryService
            .getSmartwatchHistory(deviceId, true)
            .subscribe((historyItems) => {
              this.historyItems = historyItems;
            });
        }
      });
  }

  hasDisplayValue(value: unknown): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  hasBatteryHealthWarning(): boolean {
    return this.device?.batteryHealthWarning === true;
  }

  hasChargingCyclesWarning(): boolean {
    return this.device?.chargingCyclesWarning === true;
  }

  hasBatteryHealthMissingData(): boolean {
    return (
      (this.device?.batteryHealthPercent === undefined ||
        this.device?.batteryHealthPercent === null) &&
      ScannerDetailComponent.BATTERY_HEALTH_UNRELIABLE_FIRMWARE_BUNDLE_PREFIXES.some(
        (prefix) => this.device?.firmwareBundleVersion?.startsWith(prefix),
      )
    );
  }

  firmwareVersionValue(key: string): unknown {
    return this.lastConnection?.versions?.[key];
  }

  operationDataValue(key: string): unknown {
    return this.lastConnection?.operationData?.[key];
  }

  private mergeConnections(
    connections: ScannerConnection[],
  ): ScannerConnection | undefined {
    const [latestConnection, ...fallbackConnections] = connections.slice(
      0,
      ScannerDetailComponent.CONNECTION_DATA_FALLBACK_LIMIT,
    );
    if (!latestConnection) {
      return undefined;
    }

    return {
      ...latestConnection,
      versions: this.mergeConnectionData(
        latestConnection.versions,
        fallbackConnections.map((connection) => connection.versions),
      ),
      operationData: this.mergeConnectionData(
        latestConnection.operationData,
        fallbackConnections.map((connection) => connection.operationData),
      ),
    };
  }

  private mergeConnectionData(
    preferredData: Record<string, unknown> | undefined,
    fallbackDataSets: Array<Record<string, unknown> | undefined>,
  ): Record<string, unknown> | undefined {
    const mergedData = { ...(preferredData ?? {}) };

    for (const fallbackData of fallbackDataSets) {
      if (!fallbackData) {
        continue;
      }

      for (const [key, value] of Object.entries(fallbackData)) {
        if (
          !(key in mergedData) ||
          mergedData[key] === null ||
          mergedData[key] === undefined ||
          mergedData[key] === ''
        ) {
          mergedData[key] = value;
        }
      }
    }

    return Object.keys(mergedData).length > 0 ? mergedData : undefined;
  }

  displayOperationDataValue(key: string): unknown {
    if (key === 'chargingCycles') {
      return deriveDisplayedChargingCycles(
        this.operationDataValue('chargingCycles'),
        this.operationDataValue('numberOfChargingEvents'),
      );
    }

    return this.operationDataValue(key);
  }

  shouldDisplayOperationDataField(key: string): boolean {
    return !(
      key === 'fuelgaugeFullChargeCapacityInMah' &&
      this.hasBatteryHealthMissingData()
    );
  }

  hasTechnicalData(): boolean {
    return this.firmwareVersionFields.some((field) =>
      this.hasDisplayValue(this.firmwareVersionValue(field.key)),
    );
  }

  hasOperationData(): boolean {
    return this.operationDataFields.some((field) =>
      this.shouldDisplayOperationDataField(field.key) &&
      this.hasDisplayValue(this.displayOperationDataValue(field.key)),
    );
  }

  hasSoftwareData(): boolean {
    return (
      this.hasDisplayValue(this.device?.softwareStatus) ||
      this.hasDisplayValue(this.device?.firmwareBundleName) ||
      this.device?.firmwareBundleIdentified === false ||
      this.device?.imagerFirmwareUpdateRequired === true ||
      this.hasDisplayValue(this.device?.firmwareBundleVersion) ||
      this.hasDisplayValue(this.device?.firmwareBundleLatestVersion)
    );
  }

  hasWarrantyOrCareData(): boolean {
    return (
      this.hasDisplayValue(this.device?.warrantyBaseEndDate) ||
      this.hasDisplayValue(this.device?.warrantyFullEndDate) ||
      Boolean(this.device?.careArticles?.length)
    );
  }

  isSectionOpen(section: ScannerSection): boolean {
    return this.openSections()[section];
  }

  toggleSection(section: ScannerSection): void {
    this.openSections.update((sections) => ({
      ...sections,
      [section]: !sections[section],
    }));
  }
}
